import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { RiArrowUpDownLine } from "react-icons/ri";
import { useSearchParams } from "react-router";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";
import { COLORS, ICON_ORANGE_COLOR } from "~/utils/constant";

interface SortOption {
  label: string;
  value: string;
  sortColumn: string;
}
export const ArrayOfSortValue: readonly string[] = ["relevance", "name", "releaseDate"] as const;

const SortByComponent = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const { normalInter, medium } = useFontStyle();

  // Define sort options
  const sortOptions: SortOption[] = useMemo(
    () => [
      {
        label: t("common.sort_by-video-list.relevance", { defaultValue: "Relevance" }),
        value: "relevance",
        sortColumn: "relevance",
      },
      {
        label: t("common.sort_by-video-list.name", { defaultValue: "Name" }),
        value: "name",
        sortColumn: "name",
      },
      {
        label: t("common.sort_by-video-list.releaseDate", { defaultValue: "Release date" }),
        value: "releaseDate",
        sortColumn: "record_date",
      },
    ],
    [t],
  );

  // Get current sort parameters
  const currentSortColumn = searchParams.get("sort-by");
  const currentSortDirection = searchParams.get("sort-direction");

  // Find current selected sort option
  const currentSort = useMemo(() => {
    // If no sort params, use "Sort by" as fallback
    if (!currentSortColumn) {
      return {
        label: t("common.sort_by", { defaultValue: "Sort by" }),
        value: "none",
        sortColumn: "",
      };
    }

    return sortOptions.find((option) => option.sortColumn === currentSortColumn) || sortOptions[0];
  }, [currentSortColumn, sortOptions, t]);

  // Handle sort selection with cycling - optimized for fast updates
  const handleSortChange = useCallback(
    (option: SortOption) => {
      const newParams = new URLSearchParams(searchParams);
      const isOptionAlreadyActive = currentSort.value === option.value && currentSortColumn === option.sortColumn;

      if (isOptionAlreadyActive) {
        // Option is already active, cycle through directions
        if (!currentSortDirection) {
          // Not sorted yet, apply ASC as initial direction
          newParams.set("sort-by", option.sortColumn);
          newParams.set("sort-direction", "asc");
        } else if (currentSortDirection === "asc") {
          // ASC → DESC
          newParams.set("sort-by", option.sortColumn);
          newParams.set("sort-direction", "desc");
        } else if (currentSortDirection === "desc") {
          // DESC → Remove filter
          newParams.delete("sort-by");
          newParams.delete("sort-direction");
        }
      } else {
        // New option selected - use ASC as initial direction for all options
        newParams.set("sort-by", option.sortColumn);
        newParams.set("sort-direction", "asc");
      }

      // Use replace: true for faster navigation without history entry
      setSearchParams(newParams, { replace: true });
      setIsOpen(false);
    },
    [searchParams, setSearchParams, currentSort.value, currentSortColumn, currentSortDirection],
  );

  return (
    <div className="relative">
      {/* Sort Trigger Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-4 py-1.5 rounded-[30px] border-[1.5px] transition-all duration-200 group",
          currentSortColumn ? "border-darkOrangeColor bg-darkOrangeColor/10 text-darkOrangeColor" : "border-borderPrimary bg-white   text-textColor",
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* <RiArrowUpDownLine size={16} color={isOpen || currentSortColumn ? ICON_ORANGE_COLOR : COLORS.primary} /> */}
        <span className={cn("body_text_m capitalize font-abcArizonaFlareVariable font-medium hidden lg:inline", normalInter)}>
          {currentSort.label}
        </span>

        <span className="md:hidden">
          <RiArrowUpDownLine size={16} color={isOpen || currentSortColumn ? ICON_ORANGE_COLOR : COLORS.primary} />
        </span>

        <MdOutlineKeyboardArrowDown
          className={cn(
            "hidden md:block w-5 h-5 ml-1",
            currentSortColumn ? "text-darkOrangeColor" : "text-gray-400",
            isOpen && "rotate-180 transition-all ease-in-out",
          )}
        />
      </motion.button>

      {/* Sort Options Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

            {/* Dropdown Menu */}
            <motion.div
              className="absolute top-full py-1 right-0 mt-2 w-40 bg-white border-[1.5px] border-borderPrimary rounded-2xl shadow-lg z-50 overflow-hidden"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
            >
              <div>
                {sortOptions.map((option) => {
                  const isActive = currentSort.value === option.value && currentSortColumn === option.sortColumn;
                  return (
                    <motion.button
                      key={option.value}
                      onClick={() => handleSortChange(option)}
                      className={cn(
                        "w-full px-3 py-2 text-left body_text_m transition-colors flex items-center justify-between group font-abcArizonaFlareVariable font-medium",
                        normalInter,
                        isActive ? "bg-darkOrangeColor/10 text-darkOrangeColor" : "text-textColor hover:bg-cardBg",
                      )}
                      //   whileHover={{ paddingLeft: 20 }}
                    >
                      <span>{option.label}</span>

                      <div className="flex ml-2">
                        <ArrowUp className={cn("w-4 h-4", currentSortDirection === "asc" && isActive ? "text-darkOrangeColor" : "text-gray-400")} />
                        <ArrowDown
                          className={cn("w-4 h-4", currentSortDirection === "desc" && isActive ? "text-darkOrangeColor" : "text-gray-400")}
                        />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SortByComponent;
